import { PERMANENT_COOKIE_NAME } from '@packages/domain/constants/cookies';
import { Nothing } from 'components/Nothing/Nothing';
import { NOOP } from 'constants/actions';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import type { RootState } from 'reducers/types';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import { getFieldValue } from 'selectors/ui/ui';
import { geolocation } from 'services/geolocation/geolocation';
import { cookies } from 'utils/cookies/cookies';

declare global {
  interface Window {
    adaSettings: {
      adaReadyCallback: () => void;
    };
    adaEmbed: {
      setMetaFields: (metaFields: Record<string, string>) => void;
    };
  }
}

type AdaProps = {
  onLoad: () => void;
  onShow: () => void;
};

function Ada({ onLoad = NOOP, onShow = NOOP }: AdaProps) {
  const [searchParams] = useSearchParams();
  const portalCode = [
    useSelector(getRecipientId),
    useSelector(
      state => getFieldValue(state as RootState, 'recipient') as string,
    ),
    searchParams.get('recipient'),
    searchParams.get('destination'),
  ].find(Boolean);
  const portalCodeRef = useRef(portalCode);
  const metaFieldsRef = useRef<Record<string, string>>({});
  const isReadyRef = useRef(false);
  portalCodeRef.current = portalCode;

  const handleScriptLoad = useCallback(async () => {
    const fingerprint = cookies.read(PERMANENT_COOKIE_NAME) || '';
    const location = await geolocation.fetch();
    const metaFields: Record<string, string> = {
      country: location.countryCode,
      fingerprint,
    };

    if (portalCodeRef.current) {
      metaFields.portalCode = portalCodeRef.current;
    }

    metaFieldsRef.current = metaFields;
    window.adaEmbed.setMetaFields(metaFields);
    isReadyRef.current = true;
    onLoad();
  }, [onLoad]);

  async function injectSnippet() {
    window.adaSettings = {
      adaReadyCallback: () => {
        onShow();
      },
    };

    const script = document.createElement('script');
    script.async = true;
    script.setAttribute('id', '__ada');
    script.onload = handleScriptLoad;
    script.setAttribute('data-handle', 'flywire-generative');
    script.setAttribute('src', 'https://static.ada.support/embed2.js');

    document.body.appendChild(script);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: false positive
  useEffect(() => {
    injectSnippet();
  }, []);

  useEffect(() => {
    if (!isReadyRef.current || !portalCode || !window.adaEmbed) {
      return;
    }

    const metaFields = {
      ...metaFieldsRef.current,
      portalCode,
    };
    metaFieldsRef.current = metaFields;
    window.adaEmbed.setMetaFields(metaFields);
  }, [portalCode]);

  return <Nothing />;
}

export { Ada };
